'use client';

import { useState } from 'react';

import { usePathname } from 'next/navigation';

import { cn } from '@lib';
import { imageMetaConverter } from '@lib/server';
import type { APIImageType, NestedRepeaterLink } from '@types';

import { Button, buttonVariants } from '../../components/button';
import {
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuTrigger,
} from '../../components/navigation-menu';
import { PrefetchLink } from '../../util/prefetch-link';
import { SubMenuImage } from './submenu-image';
import { SubMenuList } from './submenu-list';

/**
 * LinkOrSubMenu component renders either a simple link or a dropdown menu with potential submenus.
 * It's designed for use in navigation systems, supporting up to two levels of nested menus.
 *
 * @component
 * @param {Object} props - The component props
 * @param {NestedRepeaterLink} props.link - The link object containing label, URL, and potential submenu items
 */
export function LinkOrSubMenu({
  link,
  width,
  logo,
  image,
  subMenuImagePosition,
}: {
  link: NestedRepeaterLink;
  image: APIImageType | APIImageType[] | undefined;
  logo: APIImageType | APIImageType[];
  width: number;
  subMenuImagePosition?: 'left' | 'right';
}) {
  const logoData = imageMetaConverter(logo);
  const imageData = imageMetaConverter(image);

  const [currentImage, setCurrentImage] = useState(imageData);

  const pathname = usePathname();
  const [menuStack, setMenuStack] = useState<NestedRepeaterLink>(link);

  const buttonVariant = { size: 'sm', variant: 'ghost' } as const;
  const buttonClassName = cn('gap-3', link.url === pathname && 'bg-accent text-accent-foreground');

  // Check if the link has a submenu
  if ('subMenu' in link && link.subMenu) {
    return (
      <NavigationMenuItem>
        <NavigationMenuTrigger className={cn(buttonVariants({ className: buttonClassName, ...buttonVariant }))}>
          <NavigationMenuLink asChild>
            <PrefetchLink className="transition" href={link.url || ''}>
              {link.label}
            </PrefetchLink>
          </NavigationMenuLink>
        </NavigationMenuTrigger>

        <NavigationMenuContent style={{ width }} className="@container overflow-hidden transition-[height]">
          <div className="grid grid-cols-1 gap-6 p-6 @xl:grid-cols-2">
            <SubMenuImage
              sizes={`${width / 2}px`}
              currentImage={currentImage}
              subMenuImagePosition={subMenuImagePosition}
              menuStack={menuStack}
              logo={logoData}
            />

            <SubMenuList
              menuStack={menuStack}
              link={link}
              setMenuStack={setMenuStack}
              setCurrentImage={setCurrentImage}
            />
          </div>
        </NavigationMenuContent>
      </NavigationMenuItem>
    );
  }

  // Render a simple link if there's no submenu
  return (
    <NavigationMenuItem>
      <NavigationMenuLink asChild>
        <Button {...buttonVariant} href={link.url} className={buttonClassName}>
          {link.label}
        </Button>
      </NavigationMenuLink>
    </NavigationMenuItem>
  );
}
